import { useTranslation } from 'react-i18next';

import { useFilterPreferencesConfig } from '@/shared/features/filter-preferences/context/filter-preferences-config-context';
import { TUiPreferencesWriter } from '@/shared/features/filter-preferences/hooks/use-ui-preferences-writer/use-ui-preferences-writer.types';
import {
  commitPreferences,
  removeAllScopeViewsFromDefaults,
  removeFilterDefaultFromDefaults,
  removeScopeViewFromDefaults,
  setFilterDefaultInDefaults,
  setScopeViewInDefaults,
} from '@/shared/features/filter-preferences/hooks/use-ui-preferences-writer/use-ui-preferences-writer.utils';
import { useUserUiPreferencesLayer } from '@/shared/features/filter-preferences/hooks/use-user-ui-preferences-layer/use-user-ui-preferences-layer';
import { useSaveToast } from '@/shared/hooks/use-save-toast/use-save-toast';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import { useAppSelector } from '@/shared/redux/hooks/hooks';
import {
  TFilterDefaults,
  TSavedTableView,
  TTenantUiPreferences,
  TUserUiPreferences,
} from '@/shared/types/ui-preferences.types';

// The backend replaces the blob wholesale, so every write reads fresh and merges
// (commitPreferences) rather than submitting what this render saw.
export const useUiPreferencesWriter = (): TUiPreferencesWriter => {
  const { t } = useTranslation();
  const { run } = useSaveToast();

  const user = useAppSelector(selectUser);
  // User filter defaults are keyed by the current tenant (see TUserFilterDefaults).
  const tenantId = user?.attributes?.['custom:tenantId'] ?? '';
  const userLayer = useUserUiPreferencesLayer();

  const companyWriter = useFilterPreferencesConfig().useCompanyWriter();

  const messages = {
    loading: t('common:saving_preferences'),
    success: t('common:preferences_saved'),
    error: t('common:preferences_save_error'),
  };

  const mergeUserFilterDefaults =
    (update: (defaults?: TFilterDefaults) => TFilterDefaults) =>
    (blob: TUserUiPreferences): TUserUiPreferences => ({
      ...blob,
      filterDefaults: {
        ...blob.filterDefaults,
        [tenantId]: update(blob.filterDefaults?.[tenantId]),
      },
    });

  const withToast = (save: () => Promise<void>) => run(save, messages);

  const commitCompany = (
    merge: (blob: TTenantUiPreferences) => TTenantUiPreferences,
  ) => {
    if (!companyWriter) {
      return Promise.reject(
        new Error('No company preferences layer for this app'),
      );
    }
    return commitPreferences(companyWriter, merge);
  };

  const patchFilterDefault = (scopeKey: string, param: string, value: string) =>
    withToast(() =>
      commitPreferences(
        userLayer,
        mergeUserFilterDefaults((defaults) =>
          setFilterDefaultInDefaults(defaults, scopeKey, param, value),
        ),
      ),
    );

  const removeFilterDefault = (scopeKey: string, param: string) =>
    withToast(() =>
      commitPreferences(
        userLayer,
        mergeUserFilterDefaults((defaults) =>
          removeFilterDefaultFromDefaults(defaults, scopeKey, param),
        ),
      ),
    );

  const saveScopeView = (scopeKey: string, view: TSavedTableView) =>
    withToast(() =>
      commitPreferences(
        userLayer,
        mergeUserFilterDefaults((defaults) =>
          setScopeViewInDefaults(defaults, scopeKey, view),
        ),
      ),
    );

  const removeScopeView = (scopeKey: string) =>
    withToast(() =>
      commitPreferences(
        userLayer,
        mergeUserFilterDefaults((defaults) =>
          removeScopeViewFromDefaults(defaults, scopeKey),
        ),
      ),
    );

  // Silent by design: the only caller is a confirm dialog, which reports the
  // outcome itself, and a second toast on top of it would just be noise.
  const resetScopeViews = () =>
    commitPreferences(
      userLayer,
      mergeUserFilterDefaults(removeAllScopeViewsFromDefaults),
    );

  const setCompanyFilterDefault = (
    scopeKey: string,
    param: string,
    value: string,
  ) =>
    withToast(() =>
      commitCompany((blob) => ({
        ...blob,
        filterDefaults: setFilterDefaultInDefaults(
          blob.filterDefaults,
          scopeKey,
          param,
          value,
        ),
      })),
    );

  const removeCompanyFilterDefault = (scopeKey: string, param: string) =>
    withToast(() =>
      commitCompany((blob) => ({
        ...blob,
        filterDefaults: removeFilterDefaultFromDefaults(
          blob.filterDefaults,
          scopeKey,
          param,
        ),
      })),
    );

  return {
    patchFilterDefault,
    removeFilterDefault,
    saveScopeView,
    removeScopeView,
    resetScopeViews,
    setCompanyFilterDefault,
    removeCompanyFilterDefault,
  };
};
